/* eslint-disable class-methods-use-this */
import RequestCache from '../../utils/request-cache';
import fetchHelper from '../../utils/fetch-helper';

export default class Client {
    constructor(parent) {
        this.parent = parent;
        this.cache = new RequestCache('my-account-2-client');
    }

    flushCacheAll() {
        this.cache.removeAll();
    }

    flushCache(key) {
        this.cache.remove(key);
    }

    async getPersonalDetails() {
        if (this.cache.has('personal-details')) {
            return await this.cache.get('personal-details');
        }
        const request = fetchHelper('/gateway/api/personal-details');
        try {
            const result = await this.cache.set('personal-details', request);
            return result;
        } catch (err) {
            console.error(err);
            return {};
        }
    }

    async getDealerDetails(url, dealer, filters) {
        const key = `dealer-details-${dealer}-${filters}`;
        if (this.cache.has(key)) {
            return await this.cache.get(key);
        }
        const data = new FormData();
        data.append('dealer', dealer);
        if(filters) {
            data.append('filters', filters);
        }
        const request = fetchHelper(url, { method: 'POST', body: data });
        try {
            const result = await this.cache.set(key, request);
            return result;
        } catch (err) {
            console.error(err);
            return null;
        }
    }

    async getSavedItems(cache) {
        if (cache !== false && this.cache.has('saved-items')) {
            return await this.cache.get('saved-items');
        }
        const request = fetchHelper('/gateway/api/saved-items');
        try {
            const result = await this.cache.set('saved-items', request);
            return result;
        } catch (err) {
            console.error(err);
            return [];
        }
    }

    async postSavedItems(json) {
        const request = fetchHelper('/gateway/api/saved-items', {
            method: 'POST',
            body: JSON.stringify({ fullDataJSON: json }),
        });
        try {
            const result = await request;
            return result?.success;
        } catch (err) {
            console.error(err);
            return {};
        }
    }

    async getTestDrives() {
        if (this.cache.has('test-drives')) {
            return await this.cache.get('test-drives');
        }
        const request = fetchHelper('/gateway/api/book-a-test-drive');
        try {
            const result = await this.cache.set('test-drives', request);
            return result ?? [];
        } catch (err) {
            console.error(err);
            return [];
        }
    }

    async getOrders(cache) {
        if (cache !== false && this.cache.requestHas('orders')) {
            const result = await this.cache.requestGet('orders');
            return result?.orders ?? [];
        }
        const request = fetchHelper('/gateway/api/order');
        const req = this.cache.requestPromise(request);
        try {
            const result = await this.cache.requestSet('orders', req);
            return result?.orders;
        } catch (err) {
            console.error(err);
            return [];
        }
    }

    async getPaymentLink(order) {
        const key = `payment-link-${order.basketId}`;
        if (this.cache.has(key)) {
            return await this.cache.get(key);
        }
        const data = new FormData();
        data.set('basketId', order.basketId);
        const request = fetchHelper('/gateway/api/payment-link', {
            method: 'POST',
            body: data,
        });
        try {
            const result = await this.cache.set(key, request);
            return result ?? {};
        } catch (err) {
            console.error(err);
            return {};
        }
    }

    async getCarSavedValuations() {
        const key = 'car-valuations';
        if (this.cache.has(key)) {
            return await this.cache.get(key);
        }
        const request = fetchHelper('/gateway/api/car-valuation');

        try {
            const result = await this.cache.set(key, request);
            return result;
        } catch (err) {
            console.error(err);
            return [];
        }
    }

    async startFinanceApplication(email, orderNumber, internalProposalNumber) {
        const data = new FormData();
        data.append('type', 'start');
        data.append('email', email);
        data.append('orderNumber', orderNumber);
        data.append('internalProposalNumber', internalProposalNumber);


        const request = fetchHelper('/gateway/api/finance-application', {
            method: 'POST',
            body: data,
        });
        try {
            const response = await request;
            if (response.ok) {
                const result = await response.json();
                return result?.success;
            }
        } catch (err) {
            console.error(err);
        }
    }

    async saveFinanceApplication(formEl) {
        const data = new FormData(formEl);
        const request = fetchHelper(formEl.action, {
            method: 'POST',
            body: data,
        });
        try {
            const response = await request;
            if (response.ok) {
                const result = await response.json();
                return result?.success;
            }
        } catch (err) {
            console.error(err);
        }
    }

    async getSavedFinanceApplication(orderNumber) {
        let url = '/gateway/api/finance-application';

        if (orderNumber) {
            url = `/gateway/api/finance-application?orderNumber=${orderNumber}`;
        }

        const request = fetchHelper(url);

        try {
            const response = await request;
            if (response.ok) {
                const result = await response.json();
                return result;
            }
        } catch (err) {
            console.error(err);
        }
    }

    async getCarLeadTime(data) {
        const request = fetchHelper('/gateway/api/car-lead-time', {
            method: 'POST',
            body: JSON.stringify(data),
        });
        try {
            const response = await request;
            const result = await response.json();
            return result;
        } catch (err) {
            console.error(err);
            return null;
        }
    }

    async getDsrDetails() {
        const key = 'dsr-details';
        if (this.cache.has(key)) {
            return await this.cache.get(key);
        }

        const request = fetchHelper('/gateway/api/digital-service-record', {
            method: 'POST',
        });

        try {
            const result = await this.cache.set(key, request);
            return result;
        } catch (err) {
            console.error(err);
            return [];
        }
    }

    async getAccessoryModels(url) {
        if (this.cache.has('accessory-models')) {
            return await this.cache.get('accessory-models');
        }
        const request = fetchHelper(url);
        try {
            const result = await this.cache.set('accessory-models', request);
            return result;
        } catch (err) {
            console.error(err);
        }
    }

    async getAccessoryGroups(url,model) {
        if (this.cache.has(`accessory-groups-${model}`)) {
            return await this.cache.get(`accessory-groups-${model}`);
        }
        const request = fetchHelper(url);
        try {
            const result = await this.cache.set(`accessory-groups-${model}`, request);
            return result;
        } catch (err) {
            console.error(err);
        }
    }

}
